// PRC-023 · E1 (Mi suscripción) y E3 (cancelada, aún vigente).
// PRC-036: plantilla de Ajustes como el boceto G4: plan, uso del periodo, qué
// incluye, facturas y cancelación. Pago, facturas y cancelación abren el portal
// de Stripe y vuelven aquí.
import React, { useState } from 'react';
import { Box, ButtonBase, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import { useAuth } from '../../../context/AuthContext';
import { JourneyButton, JourneyChip } from './JourneyBasics';
import { Notice, TopBanner } from './JourneyBlocks';
import SettingsFrame, { SettingsBlock } from './SettingsFrame';
import { SkeletonBlock } from './Skeletons';
import { J } from '../journeyTokens';
import {
  CURRENT_SUBSCRIPTION_QUERY_KEY, DATA_RETENTION_DAYS, fetchCurrentSubscription, formatLongDate, formatMoney,
  formatShortDate, intervalLabel, openBillingPortal, PLAN_LABEL, type CurrentSubscription,
} from '../journeyBilling';
import { PLANS } from '../pricingPlans';
import { toSpanishNumber } from '../formatNumber';

export const useCurrentSubscription = (enabled = true) =>
  useQuery({
    queryKey: CURRENT_SUBSCRIPTION_QUERY_KEY,
    queryFn: fetchCurrentSubscription,
    enabled,
    staleTime: 5 * 60_000,
    retry: false,
  });

// PRC-010: «Domiciliación · cuenta acabada en 1234» y, mientras el banco confirma, «Cobro en curso».
const paymentMethodLabel = (data: CurrentSubscription): React.ReactNode => {
  if (data.payment_method_type === 'sepa_debit') {
    const label = data.sepa_last4 ? `Domiciliación · cuenta acabada en ${data.sepa_last4}` : 'Domiciliación';
    return data.payment_processing
      ? <>{label}<Box component="span" sx={{ display: 'block', mt: '4px', fontSize: 12.5, fontWeight: 700, color: J.amber }}>Cobro en curso</Box></>
      : label;
  }
  return data.card_last4 ? `Tarjeta acabada en ${data.card_last4}` : '';
};

const KV: React.FC<{ k: string; v: React.ReactNode }> = ({ k, v }) => (
  <Box>
    <Box sx={{ fontSize: 12.5, fontWeight: 700, color: J.muted }}>{k}</Box>
    <Box sx={{ fontSize: 15, fontWeight: 700, color: J.text, mt: '4px' }}>{v || '—'}</Box>
  </Box>
);

const usePortal = () => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const open = async () => {
    setBusy(true);
    setError(null);
    try {
      await openBillingPortal();
    } catch {
      setBusy(false);
      setError('No se pudo abrir el portal de facturación. Inténtalo de nuevo.');
    }
  };
  return { busy, error, open };
};

// Banda ámbar de E3 en las pantallas privadas, solo para el administrador.
export const CancelledSubscriptionBanner: React.FC = () => {
  const { isPricingAdmin, pricingAccess } = useAuth();
  const enabled = isPricingAdmin && pricingAccess?.state === 'paid';
  const { data } = useCurrentSubscription(enabled);
  const portal = usePortal();
  if (!enabled || !data?.has_subscription || !data.cancel_at_period_end || !data.current_period_end) return null;
  return (
    <TopBanner
      tone="amber"
      title="Has cancelado tu suscripción"
      details={[`Sigues teniendo acceso completo hasta el ${formatLongDate(data.current_period_end)}. Después, el acceso se pausa y tus datos se guardan ${DATA_RETENTION_DAYS} días.`]}
      action={<JourneyButton kind="ghost" small disabled={portal.busy} onClick={portal.open}>Mantener suscripción</JourneyButton>}
    />
  );
};

const nf = (n: number) => toSpanishNumber(n);
const addDays = (iso: string, days: number) => new Date(new Date(iso).getTime() + days * 86_400_000).toISOString();

// Barra de uso: ámbar desde el 80 %.
const UsageBar: React.FC<{ label: string; used: number | null | undefined; max: number | null | undefined; note: React.ReactNode }> = ({ label, used, max, note }) => {
  const ratio = used != null && max ? Math.min(1, used / max) : 0;
  const warn = ratio >= 0.8;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
      <Box sx={{ fontSize: 12.5, fontWeight: 700, color: J.muted }}>{label}</Box>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '6px', fontVariantNumeric: 'tabular-nums' }}>
        <Box component="span" sx={{ fontSize: 22, fontWeight: 800, color: J.text }}>{used != null ? nf(used) : '—'}</Box>
        <Box component="span" sx={{ fontSize: 13, color: J.muted }}>de {max != null ? nf(max) : '—'}</Box>
      </Box>
      <Box role="progressbar" aria-label={label} aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(ratio * 100)} sx={{ height: 6, borderRadius: 999, bgcolor: 'rgba(148,163,184,.14)', overflow: 'hidden' }}>
        <Box sx={{ width: `${ratio * 100}%`, height: '100%', bgcolor: warn ? J.amber : J.sky, borderRadius: 999 }} />
      </Box>
      <Box sx={{ fontSize: 12, color: warn ? J.amber : J.muted }}>{warn ? `${Math.round(ratio * 100)} % · cerca del límite` : note}</Box>
    </Box>
  );
};

const INVOICE_STATUS: Record<string, string> = { paid: 'Pagada', open: 'Pendiente', uncollectible: 'Impagada', void: 'Anulada' };

const MySubscriptionPanel: React.FC = () => {
  const navigate = useNavigate();
  const { data, isLoading, isError } = useCurrentSubscription();
  const portal = usePortal();

  const frame = (children: React.ReactNode, context?: string, actions?: React.ReactNode) => (
    <SettingsFrame title="Mi suscripción" context={context} actions={actions}>{children}</SettingsFrame>
  );

  if (isLoading) {
    return frame(<><SkeletonBlock h={200} radius="16px" /><SkeletonBlock h={180} radius="16px" /><SkeletonBlock h={220} radius="16px" /></>);
  }
  if (isError || !data?.has_subscription) {
    return frame(
      <Notice tone="danger">No hemos podido leer tu suscripción. <a href="#" onClick={(e) => { e.preventDefault(); void portal.open(); }}>Abrir el portal de facturación</a>.</Notice>,
    );
  }

  const plan = data.plan ? PLAN_LABEL[data.plan] : 'PROA Pricing';
  const period = intervalLabel(data.interval);
  const cancelled = !!data.cancel_at_period_end;
  const per = data.interval === 'year' ? 'al año' : 'al mes';
  const usage = data.usage;
  const included = data.plan === 'autonomy' ? PLANS.autonomy.features : PLANS.growth.features;
  const portalButton = (
    <JourneyButton small disabled={portal.busy} onClick={portal.open}>
      {portal.busy ? 'Abriendo…' : cancelled ? 'Mantener suscripción' : 'Gestionar pago y facturas'}
    </JourneyButton>
  );

  return frame(
    <>
      {portal.error && <Notice tone="danger">{portal.error}</Notice>}

      <SettingsBlock
        title={`Plan ${plan}`}
        text={cancelled
          ? <>No se renovará. Acceso hasta el <b style={{ color: J.text }}>{formatLongDate(data.current_period_end)}</b>; tus datos se guardan {DATA_RETENTION_DAYS} días.</>
          : <>Se renueva automáticamente el <b style={{ color: J.text }}>{formatLongDate(data.current_period_end)}</b>.</>}
        aside={cancelled ? <JourneyChip tone="amber">Cancelada</JourneyChip> : <JourneyChip tone="ok">Activa</JourneyChip>}
      >
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0,1fr))' }, gap: 2 }}>
          {cancelled ? (
            <>
              <KV k="Último cargo" v={data.last_charge_at ? `${formatShortDate(data.last_charge_at)} · ${formatMoney(data.last_charge_amount, data.currency)} + IVA` : ''} />
              <KV k="Datos guardados hasta" v={data.current_period_end ? formatShortDate(addDays(data.current_period_end, DATA_RETENTION_DAYS)) : ''} />
              <KV k="Facturar a" v={data.billing_name} />
            </>
          ) : (
            <>
              <KV k="Importe" v={`${formatMoney(data.amount, data.currency)} + IVA ${per}`} />
              <KV k="Método de pago" v={paymentMethodLabel(data)} />
              <KV k="Facturar a" v={data.billing_name} />
            </>
          )}
        </Box>
      </SettingsBlock>

      <SettingsBlock
        title="Uso de este mes"
        text={usage ? `Del ${formatShortDate(usage.period_start)} al ${formatShortDate(usage.period_end)}. Las filas en lotes se reinician con cada periodo.` : 'Uso del periodo actual.'}
      >
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0,1fr))' }, gap: 3 }}>
          <UsageBar label="Usuarios" used={data.users_used} max={data.users_max} note="Invita desde Usuarios del equipo" />
          <UsageBar label="Filas en lotes" used={usage?.batch_rows_used} max={usage?.batch_rows_limit} note="Se reinicia con el periodo" />
          <UsageBar label="Líneas de venta" used={usage?.sales_lines_used} max={usage?.sales_lines_limit} note="Histórico cargado" />
        </Box>
      </SettingsBlock>

      <SettingsBlock
        title="Qué incluye tu plan"
        aside={<JourneyButton kind="ghost" small onClick={() => navigate('/planes')}>Ver planes</JourneyButton>}
      >
        <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none', display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0,1fr))' }, gap: '10px 24px' }}>
          {included.map((feature) => (
            <Box component="li" key={feature} sx={{ display: 'flex', gap: '10px', alignItems: 'flex-start', fontSize: 14, color: J.body, lineHeight: 1.5 }}>
              <CheckRoundedIcon sx={{ fontSize: 18, color: J.okText, mt: '2px' }} />{feature}
            </Box>
          ))}
        </Box>
      </SettingsBlock>

      <SettingsBlock title="Facturas" text="Descárgalas en PDF. Los datos de facturación se cambian en el portal de pagos.">
        {data.invoices && data.invoices.length > 0 ? (
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ '& .MuiTableCell-root': { borderColor: J.border, color: J.body, fontVariantNumeric: 'tabular-nums' }, '& .MuiTableCell-head': { color: J.muted, fontWeight: 700, fontSize: 12.5 } }}>
              <TableHead>
                <TableRow>
                  <TableCell>Fecha</TableCell>
                  <TableCell>Número</TableCell>
                  <TableCell align="right">Base</TableCell>
                  <TableCell align="right">Total</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell align="right">PDF</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.invoices.map((inv) => (
                  <TableRow key={inv.id}>
                    <TableCell>{formatShortDate(inv.created_at)}</TableCell>
                    <TableCell>{inv.number || '—'}</TableCell>
                    <TableCell align="right">{formatMoney(inv.subtotal, inv.currency)}</TableCell>
                    <TableCell align="right">{formatMoney(inv.total, inv.currency)}</TableCell>
                    <TableCell>{INVOICE_STATUS[inv.status ?? ''] ?? inv.status ?? '—'}</TableCell>
                    <TableCell align="right">
                      {inv.invoice_pdf
                        ? <ButtonBase component="a" href={inv.invoice_pdf} target="_blank" rel="noreferrer" sx={{ color: J.skyText, fontWeight: 700, fontSize: 13 }}>PDF</ButtonBase>
                        : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        ) : (
          <Box sx={{ fontSize: 14, color: J.muted }}>Aún no hay facturas.</Box>
        )}
      </SettingsBlock>

      {/* Cancelada: «Mantener suscripción» ya está en la cabecera. */}
      {!cancelled && (
        <SettingsBlock
          title="Cancelar la suscripción"
          text={`Sigues teniendo acceso hasta el final del periodo pagado. Tus datos se guardan ${DATA_RETENTION_DAYS} días.`}
          aside={<JourneyButton kind="ghost" small disabled={portal.busy} onClick={portal.open}>Cancelar suscripción</JourneyButton>}
        />
      )}

      <Box sx={{ fontSize: 12.5, color: J.muted }}>Pago, facturas y cancelación abren el portal seguro de Stripe y vuelven aquí.</Box>
    </>,
    [plan, period].filter(Boolean).join(' · '),
    portalButton,
  );
};

export default MySubscriptionPanel;
